import { getMatomoOptions } from '../matomo';
import { getZohoOptions } from '../zoho-webassistant';

/**
 * Represents the input information required by the Tracker.
 * This interface encapsulates the essential details for identifying
 * and executing tracking scripts.
 *
 * Properties:
 * @property {string} identifier - A unique string identifier for the tracker.
 * @property {HTMLScriptElement[]} scripts - The tracking scripts to be executed.
 */
export interface TrackerInput {
  identifier: string;
  scripts: HTMLScriptElement[];
}

/**
 * Class representing a tracker for managing tracking scripts in a web document.
 */
export class Tracker {
  private readonly _trackerIdentifier: string;
  private readonly _trackingScripts: HTMLScriptElement[];
  private _status: 'attached' | 'detached' = 'detached';

  constructor({ identifier, scripts }: TrackerInput) {
    this._trackerIdentifier = identifier;
    this._trackingScripts = scripts;
  }

  private createTrackingScriptElements(): HTMLScriptElement[] {
    const scripts: HTMLScriptElement[] = [];
    for (const script of this._trackingScripts) {
      const newScript = script.cloneNode(true) as HTMLScriptElement;
      newScript.type = 'text/javascript';
      newScript.async = false;
      newScript.setAttribute(this._trackerIdentifier, ''); // Dein Identifikator
      scripts.push(newScript);
    }
    return scripts;
  }

  private getAttachedTrackingScriptElements(): HTMLScriptElement[] | null {
    return Array.from(this.appendNode.querySelectorAll(`script[${this._trackerIdentifier}]`)) as HTMLScriptElement[];
  }

  private get appendNode(): Element {
    return document.head;
  }

  // api
  public async attachTrackingScripts(): Promise<HTMLScriptElement[]> {
    const existingTracking = this.getAttachedTrackingScriptElements();
    if (existingTracking?.length) return existingTracking;
    const trackingScriptElements = this.createTrackingScriptElements();

    return Promise.all(
      trackingScriptElements.map(async trackingScriptElement => {
        return new Promise((resolve, reject) => {
          // if src is set, we have to listen to the load event.
          // -> ensure that all externally code is loaded
          trackingScriptElement.onload = e => {
            resolve(trackingScriptElement);
          };

          this.appendNode.appendChild(trackingScriptElement);
          this._status = 'attached';

          // if a source is set, the load event will be fired when all external data is loaded
          // -> only resolve when its finished.
          if (trackingScriptElement.src) return;

          // default case
          return resolve(trackingScriptElement);
        });
      }),
    );
  }

  public removeTrackingScripts(): void {
    const trackingScripts = this.getAttachedTrackingScriptElements();
    if (trackingScripts) trackingScripts.forEach(script => script.remove());
    this._status = 'detached';
  }

  public get status(): 'attached' | 'detached' {
    return this._status;
  }
}

/**
 * TrackerFactory provides methods to obtain instances of different tracking objects.
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export abstract class TrackerFactory {
  static getTracker(options: TrackerInput): Tracker {
    return new Tracker(options);
  }

  static getMatomoTracker(): Tracker | undefined {
    try {
      return this.getTracker(getMatomoOptions());
    } catch (err) {
      console.error(err);
    }
  }

  static getZohoTracker(): Tracker | undefined {
    try {
      return this.getTracker(getZohoOptions());
    } catch (err) {
      console.error(err);
    }
  }
}
